import { useAppDispatch, useAppSelector } from 'app/config/store';
import { TireStatus } from 'app/shared/model/enumerations/tire-status.model';
import { ITireTruckImplement } from 'app/shared/model/tire-truck-implement.model';
import React, { useEffect, useState } from 'react';
import { translate } from 'react-jhipster';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import TireDetail from '../component/tire-detail';
import { renderStatus } from '../component/tire-status';
import { saveAllTireTireTruckImplements } from './tire-truck-implement-custom.reducer';
import TireVehicleOdometerField from '../component/tire-vehicle-odometer-field';
import { BoxForm } from 'app/shared/layout/register/DefaultFormsComponents';
import { RegisterButton } from 'app/shared/layout/register/RegisterButton';
import { isVehicleModelTireFunctionComplete } from '../util/tire-function-completeness';

export interface ITirePositions {
  position: number;
  steppe: boolean;
  selected: boolean;
}

const TireTruckImplementCustomReplacementDialog = () => {
  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedTireOne, setSelectedTireOne] = useState<ITireTruckImplement | null>(null);
  const [selectedTireTwo, setSelectedTireTwo] = useState<ITireTruckImplement | null>(null);

  const updateSuccess = useAppSelector(state => state.tireTruckImplementCustom.updateSuccess);
  const tireTruckImplements = useAppSelector(state => state.tireTruckImplementCustom.entities);
  const truckImplementTireDataDTO = useAppSelector(state => state.tireTruckImplementCustom.truckImplementTireDataDTO);

  const [vehicleOdometer, setVehicleOdometer] = useState('');

  const tireFunctionComplete = isVehicleModelTireFunctionComplete(
    truckImplementTireDataDTO?.vehicleModel,
    truckImplementTireDataDTO?.numberOfTires
  );

  const toggle = () => {
    setIsOpen(!isOpen);
    clearAll();
  };

  useEffect(() => {
    if (isOpen && updateSuccess) {
      toggle();
    }
  }, [updateSuccess]);

  const handleTireChange = (event, setSelectedTire) => {
    const selectedTireId = Number(event.target.value);
    const selectedTire = tireTruckImplements.find(tti => tti.id === selectedTireId);
    setSelectedTire(selectedTire);
  };

  const clearAll = () => {
    setSelectedTireOne(null);
    setSelectedTireTwo(null);
    setVehicleOdometer('');
  };

  const filterOptions: ITireTruckImplement[] = tireTruckImplements
    .filter(tti => {
      const isSelectedOne = selectedTireOne?.id === tti.id;
      const isSelectedTwo = selectedTireTwo?.id === tti.id;

      return !isSelectedOne && !isSelectedTwo;
    })
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!tireFunctionComplete) {
      return toast.error(translate('fleetSenseApp.tireTruckImplement.incompleteVehicleModelError'));
    }

    if (!selectedTireOne) {
      return toast.error('Selecione o pneu 1');
    }

    if (!selectedTireTwo) {
      return toast.error('Selecione o pneu 2');
    }

    if (!vehicleOdometer) {
      return toast.error('Insira o odômetro do veículo');
    }

    const installOdometer = Number(vehicleOdometer);

    const tireOne: ITireTruckImplement = {
      id: selectedTireOne.id,
      position: selectedTireTwo.position,
      steppe: selectedTireTwo.steppe,
      tire: {
        ...selectedTireOne.tire,
        status: TireStatus.REPLACEMENT,
      },
      truckImplement: {
        id: selectedTireOne.truckImplement?.id,
      },
      installOdometer,
    };

    const tireTwo: ITireTruckImplement = {
      id: selectedTireTwo.id,
      position: selectedTireOne.position,
      steppe: selectedTireOne.steppe,
      tire: {
        ...selectedTireTwo.tire,
        status: TireStatus.REPLACEMENT,
      },
      truckImplement: {
        id: selectedTireTwo.truckImplement?.id,
      },
      installOdometer,
    };

    dispatch(saveAllTireTireTruckImplements([tireOne, tireTwo]));
  };

  return (
    <>
      <Button variant="contained" color="info" startIcon={<SyncIcon />} onClick={() => toggle()}>
        Trocar
      </Button>

      {isOpen && (
        <Dialog
          open={isOpen}
          onClose={toggle}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 1,
              bgcolor: 'primary.main',
              backgroundImage: 'none',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pb: 1.5,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Trocar pneus
            </Typography>
            <IconButton onClick={toggle} size="small" aria-label="Fechar">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <Divider />

          <BoxForm onSubmit={handleSubmit}>
          <DialogContent sx={{ py: 2.5 }}>
            {!tireFunctionComplete && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {translate('fleetSenseApp.tireTruckImplement.incompleteVehicleModelWarning')}
              </Alert>
            )}
            <TireVehicleOdometerField
              truckImplementId={tireTruckImplements[0]?.truckImplement?.id ?? ''}
              value={vehicleOdometer}
              onChange={setVehicleOdometer}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ bgcolor: 'background.paper', backgroundImage: 'none' }}>
                  <CardContent>
                    <Typography component="div" gutterBottom>
                      Selecione o pneu 1 {renderStatus(TireStatus.INSTALLED)}
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      name="tireOne"
                      id="tireOne"
                      value={selectedTireOne?.id ?? ''}
                      onChange={e => handleTireChange(e, setSelectedTireOne)}
                      SelectProps={{
                        MenuProps: {
                          variant: 'menu',
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: { sx: { mt: 1 } },
                        },
                      }}
                    >
                      {filterOptions.map((tti, i) => (
                        <MenuItem value={tti.id ?? ''} key={i}>
                          {tti?.tire?.marking} - {tti?.position}
                        </MenuItem>
                      ))}
                    </TextField>
                    {selectedTireOne?.tire && (
                      <TireDetail
                        tire={selectedTireOne.tire}
                        position={selectedTireOne.position ?? undefined}
                        steppe={selectedTireOne.steppe ?? undefined}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ bgcolor: 'background.paper', backgroundImage: 'none' }}>
                  <CardContent>
                    <Typography component="div" gutterBottom>
                      Selecione o pneu 2 {renderStatus(TireStatus.INSTALLED)}
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      name="tireTwo"
                      id="tireTwo"
                      value={selectedTireTwo?.id ?? ''}
                      onChange={e => handleTireChange(e, setSelectedTireTwo)}
                      SelectProps={{
                        MenuProps: {
                          variant: 'menu',
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: { sx: { mt: 1 } },
                        },
                      }}
                    >
                      {filterOptions.map((tti, i) => (
                        <MenuItem value={tti.id ?? ''} key={i}>
                          {tti?.tire?.marking} - {tti?.position}
                        </MenuItem>
                      ))}
                    </TextField>
                    {selectedTireTwo?.tire && (
                      <TireDetail
                        tire={selectedTireTwo.tire}
                        position={selectedTireTwo.position ?? undefined}
                        steppe={selectedTireTwo.steppe ?? undefined}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </DialogContent>

          <Divider />

          <Box sx={{ px: 3, py: 2 }}>
            <RegisterButton onBack={() => setIsOpen(false)} />
          </Box>
          </BoxForm>
        </Dialog>
      )}
    </>
  );
};

export default TireTruckImplementCustomReplacementDialog;
